import React, { useMemo } from "react";
import "./styles.scss";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import {
  ACCOUNT,
  ACCOUNT_WITH_TRACKING_ID,
  ANALYTICS,
  ANALYTICS_WITH_TRACKING_ID,
  BILLS,
  BILLS_WITH_TRACKING,
  COLORTEST,
  CONTACT,
  FORM_DATA,
  FORM_DATA_WITH_TRACKING,
  JETPACK_CONFIG,
  JETPACK_CONFIG_WITH_TRACKING,
  LEGAL,
  LOTTERY_STATS,
  MONTHLY_STATS,
  MONTHLY_STATS_DETAIL,
  MONTHLY_STATS_DETAIL_WITH_TRACKING,
  MONTHLY_STATS_WITH_TRACKING,
  POSA,
  POSA_WITH_DELIVERY_ID,
  POSA_WITH_TRACKING_ID,
  SAAS_FLYERS_ORDER,
  SAAS_FLYERS_ORDER_WITH_TRACKING_ID,
  SUBSCRIPTION,
  SUBSCRIPTION_WITH_TRACKING
} from "../../../utils/routes/routes";
import { FormData } from "../../08_FormData/FormData";
import SaasFlyersOrderContainer from "../../../components/SaasFlyersOrder/SaasFlyersOrderContainer";
import Bills from "../../02_Bills/Bills";
import { Contact } from "../../09_Contact/Contact";
import POSAManager from "../../12_POSAManager/POSAManager";
import { Analytics } from "../../04_Analytics/Analytics";
import Layout from "../Layout";
import { JetpackConfiguratorWrapper } from "../../19_JetpackConfig/JetpackConfiguratorWrapper";
import CONFIG from "../../../model/TSConfig";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import {
  buildPathWithTrackingId,
  useTrackingIdRouting
} from "../../../utils/routing/routingUtils";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Separator from "../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../designsystem/ds_commons";
import { Sidebar } from "../Sidebar/Sidebar";
import CheckoutSubscription from "../../17_JetpackOnboarding/components/CheckoutSubscription/Checkout";
import { useJetpackConfigSelector } from "../../../redux/jetpackConfig";
import { BusinessProfile } from "../../21_BusinessProfile/BusinessProfile";
import ColorTest from "../../24_ColorTest/ColorTest";
import MonthlyStats from "../../04_Analytics/components/monthly/MonthlyStats";
import MonthlyStatsDetail from "../../04_Analytics/components/monthly/MonthlyStatDetail";
import { tt } from "../../../utils/i18n/i18n";
import { MobileHeader } from "../Mobile/MobileHeader";
import MobileDetect from "mobile-detect";
import QRCode from "qrcode";
import MobilePreview from "../../../components/MobilePreview/MobilePreview";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import UploadIcon from "../../../designsystem/icons/UploadIcon";
import { QRCodeModal, QRCODE_OPTIONS } from "./QRCodeModal";
import NiceModal from "@ebay/nice-modal-react";
import {
  checkJetpackCompleteness,
  CompletenessCheckResult
} from "../../../utils/helpers/checkJetpackCompleteness";
import PostPaymentModal from "../../../components/modal/ConfirmPayment/PostPaymentModal";
import TwemojiWrapper from "../../../components/TwemojiWrapper/TwemojiWrapper";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Heading4 from "../../../designsystem/typography/KC-Heading/KC_Heading4";
import Legal from "../../25_Legal/Legal";

export const LayoutWithNavbar = () => {
  const history = useHistory();
  const location = useLocation();
  useTrackingIdRouting("LayoutWithNavbar"); // Handle trackingID from URL
  const { accountToDisplay } = useAccountToDisplaySelector();
  const jetpackConfig = useJetpackConfigSelector();

  const isShowPreviewMobile = useMemo(() => {
    const showPreviewInConfig = location.pathname.includes("config");
    const hasColors = !!jetpackConfig?.colors;
    const shouldShow = (hasColors && showPreviewInConfig) ?? false;

    if (!shouldShow) {
      const reasons = [];
      if (!hasColors) {
        reasons.push("jetpackConfig.colors is missing/falsy");
      }
      if (!showPreviewInConfig) {
        reasons.push(
          `pathname "${location.pathname}" does not include "config"`
        );
      }
      console.warn(
        "[MobilePreview] preview hidden because:",
        reasons.join(" AND "),
        { hasColors, showPreviewInConfig, jetpackConfig }
      );
    }

    return shouldShow;
  }, [jetpackConfig, location.pathname]);

  const realGameURL = useMemo(() => {
    if (!jetpackConfig) {
      return "";
    }
    return `${CONFIG.jetpackFrontendUrl}?v=${jetpackConfig.configId}`;
  }, [jetpackConfig]);

  const iframeGameURL = useMemo(() => {
    return `${CONFIG.jetpackFrontendUrl}?v=${jetpackConfig?._id}&pre=true`;
  }, [jetpackConfig]);

  const jetpackIsCompleteResult = useMemo<CompletenessCheckResult>(() => {
    return checkJetpackCompleteness(
      jetpackConfig,
      !!accountToDisplay?.saasFeatures?.ml
    );
  }, [accountToDisplay, jetpackConfig]);

  async function showJetpackIncompleteModal(
    isMobile: string | null,
    dataURL: string | undefined
  ) {
    const gameText = jetpackIsCompleteResult.hasGame
      ? tt("incomplete_game_game_present")
      : tt("incomplete_game_game_missing");
    const prizeText = jetpackIsCompleteResult.hasPrize
      ? tt("incomplete_game_prize_present")
      : tt("incomplete_game_prize_missing");
    const ctaText = jetpackIsCompleteResult.hasCta
      ? tt("incomplete_game_cta_present")
      : tt("incomplete_game_cta_missing");

    const bodyText = (
      <TwemojiWrapper isText={true}>
        {tt("incomplete_game_intro")}
        <br />
        <br />
        {gameText}
        <br />
        {prizeText}
        <br />
        {ctaText}
      </TwemojiWrapper>
    );

    await NiceModal.show(PostPaymentModal, {
      headerText: tt("incomplete_game"),
      bodyText,
      buttonText: tt("ok"),
      onClick: () => {
        history.push(
          buildPathWithTrackingId(JETPACK_CONFIG, accountToDisplay?.trackingID)
        );
      },
      additionalContent: (
        <div className="d-flex flex-column align-items-center">
          <KC_Separator size={KC_Sizes.l} />
          <TwemojiWrapper isText={true}>
            {isMobile && (
              <KC_Heading4 text={tt("incomplete_game_test_warning_link")} />
            )}
            {!isMobile && (
              <KC_Heading4 text={tt("incomplete_game_test_warning_qr")} />
            )}
          </TwemojiWrapper>
          <KC_Separator size={KC_Sizes.reg} />
          {isMobile && (
            <KC_UIText
              weight="reg"
              text={
                <>
                  <a href={realGameURL}>{realGameURL}</a>
                </>
              }
            />
          )}
          {!isMobile && (
            <>
              <img
                className="img-responsive"
                src={dataURL}
                style={{
                  width: 250,
                  height: 250
                }}
              />
            </>
          )}
        </div>
      )
    });
    return;
  }

  async function onTestGameButtonClicked() {
    const md = new MobileDetect(window.navigator.userAgent);
    const isMobile = md.mobile();

    const dataURL = isMobile
      ? ""
      : await QRCode.toDataURL(realGameURL, QRCODE_OPTIONS);

    if (!jetpackIsCompleteResult.isComplete) {
      void showJetpackIncompleteModal(isMobile, dataURL);
      return;
    }

    if (md.mobile()) {
      window.open(realGameURL, "_blank");
    } else {
      void NiceModal.show(QRCodeModal, {
        dataURL,
        realGameURL
      });
    }
  }

  const renderPreviewMobile = (
    iframeGameURL: string,
    isShowPreviewMobile: boolean
  ) => {
    if (!isShowPreviewMobile) {
      return null;
    }

    return (
      <div className="d-flex flex-column w-100 w-sm-auto flex-grow-0 flex-sm-grow-1">
        <div className="d-flex align-items-center justify-content-between">
          <KC_Heading3 text={tt("yourGameHeading")} />
          <div>
            <KC_Button
              size={KC_ButtonSize.Large}
              customIcon={<UploadIcon />}
              isInverted={true}
              showIconOnTheLeft={true}
              text={tt("testMyGameButton")}
              onClick={onTestGameButtonClicked}
              className="d-flex"
            />
          </div>
        </div>
        <KC_Separator size={KC_Sizes.sm} />
        <KC_Separator size={KC_Sizes.sm} />
        <MobilePreview iframeGameURL={iframeGameURL} />
      </div>
    );
  };

  return (
    <>
      <Layout
        showSidebar={true}
        sideBar={<Sidebar />}
        showHeader={true}
        header={
          <div className="d-flex align-items-center justify-content-between w-100">
            <MobileHeader />
          </div>
        }
        previewMobile={renderPreviewMobile(iframeGameURL, isShowPreviewMobile)}
      >
        <Switch>
          <Route path={BILLS_WITH_TRACKING}>
            <Bills />
          </Route>
          <Route path={BILLS}>
            <Bills />
          </Route>

          <Route path={MONTHLY_STATS_DETAIL_WITH_TRACKING}>
            <MonthlyStatsDetail />
          </Route>
          <Route path={MONTHLY_STATS_DETAIL} exact>
            <MonthlyStatsDetail />
          </Route>

          <Route path={MONTHLY_STATS_WITH_TRACKING}>
            <MonthlyStats />
          </Route>
          <Route path={MONTHLY_STATS}>
            <MonthlyStats />
          </Route>

          <Route path={SUBSCRIPTION_WITH_TRACKING}>
            <CheckoutSubscription />
          </Route>
          <Route path={SUBSCRIPTION}>
            <CheckoutSubscription />
          </Route>

          <Route path={LOTTERY_STATS}>
            <Redirect to={ANALYTICS} />
          </Route>
          <Route path={ANALYTICS_WITH_TRACKING_ID}>
            <Analytics />
          </Route>
          <Route path={ANALYTICS} exact>
            <Analytics />
          </Route>

          <Route path={JETPACK_CONFIG_WITH_TRACKING}>
            <JetpackConfiguratorWrapper />
          </Route>
          <Route path={JETPACK_CONFIG}>
            <JetpackConfiguratorWrapper />
          </Route>

          <Route path={FORM_DATA_WITH_TRACKING}>
            <FormData />
          </Route>
          <Route path={FORM_DATA} exact>
            <FormData />
          </Route>

          <Route path={POSA_WITH_DELIVERY_ID}>
            <POSAManager />
          </Route>
          <Route path={POSA_WITH_TRACKING_ID}>
            <POSAManager />
          </Route>
          <Route path={POSA}>
            <POSAManager />
          </Route>

          <Route path={SAAS_FLYERS_ORDER_WITH_TRACKING_ID}>
            <SaasFlyersOrderContainer />
          </Route>
          <Route path={SAAS_FLYERS_ORDER}>
            <SaasFlyersOrderContainer />
          </Route>

          <Route path={ACCOUNT_WITH_TRACKING_ID}>
            <BusinessProfile />
          </Route>
          <Route path={ACCOUNT} exact>
            <BusinessProfile />
          </Route>

          <Route path={LEGAL}>
            <Legal />
          </Route>
          <Route path={CONTACT}>
            <Contact />
          </Route>
          <Route path={COLORTEST}>
            <ColorTest />
          </Route>
        </Switch>
      </Layout>
    </>
  );
};
